import { useState } from "react";
import { useLanguage } from "../LanguageContext";

export default function ContactView() {
  const { isAr } = useLanguage();
  const t = (en, ar) => (isAr ? ar : en);

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.id]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const subject = `Project Inquiry: ${form.name}`;
    const bodyText = `Full Name: ${form.name}\r\nEmail: ${form.email}\r\nPhone: ${form.phone || "N/A"}\r\n\r\nProject Details:\r\n${form.message}`;
    
    window.location.href = `mailto:info@axionrailway.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
  };

  const contactInfo = [
    {
      icon: "ph-bold ph-map-pin",
      title: t("Headquarters", "المقر الرئيسي"),
      lines: isAr
        ? ["طريق الملك فهد، حي العليا، الرياض 12211، المملكة العربية السعودية"]
        : ["King Fahd Road, Olaya District, Riyadh 12211, Saudi Arabia"],
    },
    {
      icon: "ph-bold ph-envelope-simple",
      title: t("Email Us", "راسلنا"),
      lines: ["info@axionrailway.com"],
    },
    {
      icon: "ph-bold ph-phone",
      title: t("Call Us", "اتصل بنا"),
      lines: ["+966 50 348 5621"],
    },
  ];

  return (
    <div className="view-enter pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 bg-secondary-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h3 className="text-primary-700 font-bold tracking-wider uppercase mb-2 text-sm font-display">
            {t("Get in Touch", "تواصل معنا")}
          </h3>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-neutral-900 mb-4 font-display">
            {t("Contact Us", "اتصل بنا")}
          </h1>
          <h2 className="text-base sm:text-lg md:text-xl text-neutral-600 font-light font-display">
            {t("Let's discuss your next infrastructure project.", "دعونا نناقش مشروعك القادم للبنية التحتية.")}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Info Panel */}
          <div className="lg:col-span-2 bg-primary-600 text-white p-6 sm:p-8 md:p-10 lg:p-14 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute -bottom-24 -right-24 w-64 h-64 border-[40px] border-white/10 rounded-full" />
            <div className="absolute -top-12 -left-12 w-32 h-32 border-[20px] border-white/10 rounded-full" />
            <div className="relative z-10 bg-gradient-to-br from-primary-600 to-primary-700 p-6 sm:p-8 md:p-10 lg:p-12 rounded-3xl shadow-large">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-black mb-6 sm:mb-8 md:mb-12 font-display text-white">
                {t("Contact Information", "معلومات التواصل")}
              </h3>
              <div className="space-y-6 sm:space-y-8 md:space-y-10">
                {contactInfo.map(({ icon, title, lines }) => (
                  <div key={title} className="flex items-start gap-3 sm:gap-4 md:gap-5">
                    <div className="w-10 h-10 sm:w-12 md:w-12 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                      <i className={`${icon} text-lg sm:text-xl md:text-2xl`} style={{ color: "rgb(193, 160, 84)" }} />
                    </div>
                    <div>
                      <h4 className="font-bold text-base sm:text-lg mb-1 font-display">{title}</h4>
                      {lines.map((line, i) => (
                        <p key={i} className="text-white/70 font-light leading-relaxed" dir={line.startsWith("+") ? "ltr" : undefined}>
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 p-6 sm:p-8 md:p-10 lg:p-14">
            <form onSubmit={handleSubmit}>
              <div className="mb-4 sm:mb-5 md:mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2 tracking-wide" htmlFor="name">
                  {t("Full Name", "الاسم الكامل")} <span className="text-red-500">*</span>
                </label>
                <input
                  id="name" type="text" value={form.name} onChange={handleChange} required
                  className="w-full bg-neutral-100 border border-neutral-300 rounded-xl py-2.5 sm:py-3 px-3 sm:px-4 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all"
                />
              </div>
              <div className="mb-4 sm:mb-5 md:mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2 tracking-wide" htmlFor="email">
                  {t("Email Address", "البريد الإلكتروني")} <span className="text-red-500">*</span>
                </label>
                <input
                  id="email" type="email" value={form.email} onChange={handleChange} required
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-md py-2.5 sm:py-3 px-3 sm:px-4 focus:outline-none focus:ring-2 focus:ring-axion-base focus:bg-white transition"
                  dir="ltr"
                />
              </div>
              <div className="mb-4 sm:mb-5 md:mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2 tracking-wide" htmlFor="phone">
                  {t("Phone Number (Optional)", "رقم الهاتف (اختياري)")}
                </label>
                <input
                  id="phone" type="tel" value={form.phone} onChange={handleChange}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-md py-2.5 sm:py-3 px-3 sm:px-4 focus:outline-none focus:ring-2 focus:ring-axion-base focus:bg-white transition"
                  dir="ltr"
                />
              </div>
              <div className="mb-6 sm:mb-7 md:mb-8">
                <label className="block text-gray-700 text-sm font-bold mb-2 tracking-wide" htmlFor="message">
                  {t("Project Details", "تفاصيل المشروع")} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message" value={form.message} onChange={handleChange} required
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-md py-3 px-4 h-32 focus:outline-none focus:ring-2 focus:ring-axion-base focus:bg-white transition resize-none"
                />
              </div>
              <button
                type="submit"
                className="bg-axion-base text-white font-bold tracking-wide py-3 sm:py-4 px-6 sm:px-8 rounded-md w-full hover:bg-axion-light transition-all shadow-lg transform hover:-translate-y-0.5 flex justify-center items-center gap-2"
              >
                {t("Send Message", "إرسال الرسالة")}
                <i className="ph-bold ph-paper-plane-tilt" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
